import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOidcContext } from '../contexts/OidcContext';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { api } from '../api';
import PendingMembersSection from '../components/PendingMembersSection';
import {
  HubSeatOut as HubSeat,
  PortalOrgMemberOut as OrgMember,
} from '../types/apiRequests';
import { useOrgInfo } from '../contexts/OrgInfoContext';
import { useBeheerder } from '../hooks/useBeheerder';
import { useTranslation } from '../i18n/useTranslation';

function AddSeatDialog({
  open,
  onClose,
  onAdded,
}: {
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
}) {
  const { getAccessTokenSilently } = useOidcContext();
  const t = useTranslation();
  const [options, setOptions] = useState<OrgMember[]>([]);
  const [selected, setSelected] = useState<OrgMember | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (inputValue.length < 2) {
      setOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const token = await getAccessTokenSilently();
        const results = await api.searchOrgMembers(token, inputValue);
        setOptions(results);
      } catch {
        setOptions([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue, getAccessTokenSilently]);

  const handleSubmit = async () => {
    if (!selected) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      await api.addSeat(token, {
        auth0_user_id: selected.user_id,
        auth0_email: selected.email || '',
      });
      onAdded();
      handleClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('errors.genericError'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelected(null);
    setInputValue('');
    setOptions([]);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{t('seats.assignDialog.title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Autocomplete
            options={options}
            value={selected}
            onChange={(_e, value) => setSelected(value)}
            inputValue={inputValue}
            onInputChange={(_e, value) => setInputValue(value)}
            getOptionLabel={(option) =>
              option.name
                ? `${option.name} (${option.email || option.user_id})`
                : option.email || option.user_id
            }
            isOptionEqualToValue={(option, value) => option.user_id === value.user_id}
            filterOptions={(x) => x}
            loading={searching}
            noOptionsText={
              inputValue.length < 2
                ? t('seats.assignDialog.searchMinChars')
                : t('seats.assignDialog.noUsers')
            }
            renderOption={(props, option) => (
              <li {...props} key={option.user_id}>
                <Box>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                    {option.name || option.email || option.user_id}
                  </Typography>
                  {option.email && option.name && (
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      {option.email}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('seats.assignDialog.searchLabel')}
                placeholder={t('seats.assignDialog.searchPlaceholder')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searching ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selected || loading}
          disableElevation
          sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' }, fontWeight: 600 }}
        >
          {loading ? t('seats.assignDialog.assigningButton') : t('seats.assignDialog.assignButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function SeatsPage() {
  const { getAccessTokenSilently } = useOidcContext();
  const navigate = useNavigate();
  const t = useTranslation();
  const isBeheerder = useBeheerder();
  const orgInfo = useOrgInfo();
  const [seats, setSeats] = useState<HubSeat[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingChatId, setTogglingChatId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadSeats = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      setSeats(await api.getSeats(token));
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (!isBeheerder) return;
    loadSeats();
  }, [isBeheerder, loadSeats]);

  const handleToggleChat = async (seatId: string) => {
    setTogglingChatId(seatId);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const updated = await api.toggleSeatChat(token, seatId);
      setSeats((prev) => prev.map((s) => (s.id === seatId ? updated : s)));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('errors.genericError'));
    } finally {
      setTogglingChatId(null);
    }
  };

  const handleDelete = async (seatId: string) => {
    if (!window.confirm(t('seats.deleteConfirm'))) return;
    setDeletingId(seatId);
    try {
      const token = await getAccessTokenSilently();
      await api.deleteSeat(token, seatId);
      setSeats((prev) => prev.filter((s) => s.id !== seatId));
    } finally {
      setDeletingId(null);
    }
  };

  if (!isBeheerder) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Card
          sx={{
            maxWidth: 440,
            p: 5,
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: '12px',
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            {t('seats.accessDenied.title')}
          </Typography>
          <Typography sx={{ color: 'text.secondary', mb: 3 }}>
            {t('seats.accessDenied.body')}
          </Typography>
          <Button onClick={() => navigate('/')} variant="outlined" size="small">
            {t('common.backToOverview')}
          </Button>
        </Card>
      </Box>
    );
  }

  const seatCount = orgInfo
    ? orgInfo.hub_tier === 'enterprise'
      ? `${seats.length}`
      : `${seats.length} / ${orgInfo.seat_max}`
    : '';

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 5,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ mb: 0.5, fontWeight: 700, letterSpacing: '-0.02em', color: 'text.primary' }}
          >
            {t('seats.page.title')}
          </Typography>
          {orgInfo && (
            <Typography sx={{ color: 'text.secondary', fontSize: '0.95rem' }}>
              {seatCount} {t('seats.page.subtitle')}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          disableElevation
          sx={{
            bgcolor: 'primary.main',
            '&:hover': { bgcolor: 'primary.dark' },
            fontWeight: 600,
            borderRadius: '10px',
            px: 2.5,
          }}
        >
          {t('seats.addButton')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px' }}>
          {error}
        </Alert>
      )}

      <PendingMembersSection onApproved={loadSeats} />

      <Card
        sx={{
          border: '1px solid rgba(0,0,0,0.08)',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'background.default' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary' }}>
                {t('seats.col.email')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary' }}>
                {t('seats.col.assignedBy')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary' }}>
                {t('common.col.date')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', width: 80 }}>
                {t('seats.col.chat')}
              </TableCell>
              <TableCell sx={{ width: 60 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton variant="text" width="60%" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width="50%" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width="40%" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="rounded" width={34} height={20} />
                    </TableCell>
                    <TableCell />
                  </TableRow>
                ))
              : seats.map((seat) => (
                  <TableRow
                    key={seat.id}
                    hover
                    sx={{ '&:last-child td': { border: 0 } }}
                  >
                    <TableCell>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                        {seat.auth0_email}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: 'text.disabled',
                          fontFamily: "'JetBrains Mono', monospace",
                        }}
                      >
                        {seat.auth0_user_id}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                      {seat.assigned_by}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {new Date(seat.created).toLocaleDateString('nl-NL', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={seat.chat_enabled ? t('seats.chat.disable') : t('seats.chat.enable')}>
                        <Switch
                          size="small"
                          checked={seat.chat_enabled}
                          disabled={togglingChatId === seat.id}
                          onChange={() => handleToggleChat(seat.id)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: 'primary.main' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              bgcolor: 'primary.main',
                            },
                          }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {seat.assigned_by !== 'system' && (
                        <Tooltip title={t('seats.deleteTooltip')}>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(seat.id)}
                            disabled={deletingId === seat.id}
                            sx={{ color: 'text.disabled', '&:hover': { color: '#C24242' } }}
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            {!loading && seats.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} sx={{ textAlign: 'center', py: 8 }}>
                  <Typography sx={{ color: 'text.disabled' }}>{t('seats.table.empty')}</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <AddSeatDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onAdded={loadSeats}
      />
    </>
  );
}
